import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { type Board, useCreateBoard } from '@/entities/board';
import {
  ASSIGNABLE_ROLES,
  type MemberRole,
  roleLabelKey,
  UserSelect,
  useBoardLabelCatalog,
  useCustomRoles,
  useTeams,
  useWorkspaceMembers,
} from '@/entities/collab';
import { useViewer } from '@/entities/viewer';
import { useWorkspace } from '@/entities/workspace';
import { FormError, useFieldError, useServerErrors } from '@/shared/forms';
import { can, PERMISSIONS } from '@/shared/lib/permissions';
import {
  Button,
  Combobox,
  DialogActions,
  IconButton,
  Icons,
  MultiSelect,
  TextAreaField,
  TextField,
} from '@/shared/ui';

import styles from './BoardManage.module.css';

const schema = z.object({
  name: z.string().trim().min(1, 'REQUIRED').max(200, 'STRING_TOO_LONG'),
  description: z.string().max(5000, 'STRING_TOO_LONG'),
});
type Values = z.infer<typeof schema>;

interface Grant {
  id: string;
  role: MemberRole;
}

/**
 * A board is private by default: only its owner (the creator) and the workspace owner see it.
 * In a TEAM workspace the creator can set up the initial collaboration at once - the team the
 * board is created for (succession, not access), labels, teams with access and participants -
 * and everything is created in one transaction, so nobody sees a half-configured board.
 */
export function CreateBoardForm({
  workspaceId,
  sectionId,
  onCreated,
  onCancel,
}: {
  workspaceId: string;
  /** File the new board in this section in the same request (UD-STRUCT). */
  sectionId?: string | null | undefined;
  onCreated: (board: Board) => void;
  /** When given, a Cancel button sits next to Create (dialog use). */
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const fieldError = useFieldError();
  const create = useCreateBoard(workspaceId);
  const workspace = useWorkspace(workspaceId);
  const isTeam = workspace.data?.workspace_type === 'team';
  const viewer = useViewer();
  const viewerId = viewer.status === 'authenticated' ? viewer.user.id : null;
  const teams = useTeams(workspaceId, isTeam);
  const members = useWorkspaceMembers(workspaceId, isTeam);
  const labels = useBoardLabelCatalog(workspaceId, isTeam);
  const customRoles = useCustomRoles(workspaceId, isTeam);
  const [origin, setOrigin] = useState<string | null>(null);
  const [labelIds, setLabelIds] = useState<string[]>([]);
  const [teamAccess, setTeamAccess] = useState<Grant[]>([]);
  const [participants, setParticipants] = useState<Grant[]>([]);
  const [roleAccess, setRoleAccess] = useState<Grant[]>([]);
  const [personPick, setPersonPick] = useState<string | null>(null);
  const { register, handleSubmit, formState, setError, reset } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', description: '' },
  });
  const serverErrors = useServerErrors(setError, ['name']);

  const mayManageTeams = can(workspace.data?.permissions, PERMISSIONS.workspaceManageTeams);
  const ownTeams = (teams.data ?? []).filter(
    (team) => mayManageTeams || (viewerId !== null && team.member_ids.includes(viewerId)),
  );
  const teamName = (id: string) => (teams.data ?? []).find((x) => x.id === id)?.name ?? '…';
  const roleName = (id: string) =>
    id.startsWith('custom:')
      ? ((customRoles.data ?? []).find((x) => x.id === id.slice(7))?.name ?? '…')
      : t(roleLabelKey(id.slice(5) as MemberRole));
  const personName = (id: string) =>
    (members.data ?? []).find((x) => x.user_id === id)?.display_name ?? '…';

  const onSubmit = handleSubmit((values) => {
    serverErrors.clear();
    create.mutate(
      {
        name: values.name,
        ...(sectionId && { section_id: sectionId }),
        ...(isTeam && values.description.trim() && { description: values.description.trim() }),
        ...(isTeam && origin !== null && { origin_team_id: origin }),
        ...(isTeam && labelIds.length > 0 && { label_ids: labelIds }),
        ...(isTeam &&
          teamAccess.length > 0 && {
            team_access: teamAccess.map((g) => ({ team_id: g.id, role: g.role })),
          }),
        ...(isTeam &&
          roleAccess.length > 0 && {
            role_access: roleAccess.map((g) =>
              g.id.startsWith('custom:')
                ? { custom_role_id: g.id.slice(7), role: g.role }
                : { subject_role: g.id.slice(5), role: g.role },
            ),
          }),
        ...(isTeam &&
          participants.length > 0 && {
            participants: participants.map((g) => ({ user_id: g.id, role: g.role })),
          }),
      },
      {
        onSuccess: (board) => {
          reset();
          onCreated(board);
        },
        onError: serverErrors.handle,
      },
    );
  });

  const roleOptions = ASSIGNABLE_ROLES.map((r) => ({ value: r, label: t(`roles.${r}`) }));

  return (
    <form className={styles.createForm} onSubmit={(e) => void onSubmit(e)} noValidate>
      <TextField
        label={t('boards.newBoardName')}
        autoFocus
        placeholder={t('boards.newBoardPlaceholder')}
        error={fieldError(formState.errors.name?.message)}
        {...register('name')}
      />
      {isTeam && (
        <div className={styles.teamOptions}>
          <TextAreaField label={t('boards.description')} rows={3} {...register('description')} />

          <div className={styles.optionBlock}>
            <span className={styles.optionTitle}>{t('boards.originTeam')}</span>
            <p className={styles.optionHint}>{t('boards.originTeamHint')}</p>
            <Combobox
              label={t('boards.originTeam')}
              value={origin ?? '__none__'}
              size="sm"
              searchPlaceholder={t('common.search')}
              emptyText={t('common.noResults')}
              options={[
                { value: '__none__', label: t('boards.noOriginTeam') },
                ...ownTeams.map((team) => ({ value: team.id, label: team.name })),
              ]}
              onChange={(next) => {
                setOrigin(next === '__none__' ? null : next);
              }}
            />
          </div>

          {(labels.data ?? []).length > 0 && (
            <div className={styles.optionBlock}>
              <span className={styles.optionTitle}>{t('boards.initialLabels')}</span>
              <MultiSelect
                label={t('boards.initialLabels')}
                options={(labels.data ?? []).map((label) => ({
                  value: label.id,
                  label: label.name,
                  color: label.color,
                }))}
                value={labelIds}
                onChange={setLabelIds}
              />
            </div>
          )}

          <div className={styles.optionBlock}>
            <span className={styles.optionTitle}>{t('boards.teamsWithAccess')}</span>
            <p className={styles.optionHint}>{t('boards.teamsWithAccessHint')}</p>
            <ul className={styles.grantList}>
              {teamAccess.map((grant) => (
                <li key={grant.id} className={styles.grantRow}>
                  <span className={styles.grantName}>{teamName(grant.id)}</span>
                  <Combobox
                    label={t('access.roleOf', { name: teamName(grant.id) })}
                    value={grant.role}
                    size="sm"
                    options={roleOptions}
                    onChange={(next) => {
                      setTeamAccess(
                        teamAccess.map((g) =>
                          g.id === grant.id ? { ...g, role: next as MemberRole } : g,
                        ),
                      );
                    }}
                  />
                  <IconButton
                    label={t('boards.removeGrant', { name: teamName(grant.id) })}
                    icon={<Icons.close />}
                    onClick={() => {
                      setTeamAccess(teamAccess.filter((g) => g.id !== grant.id));
                    }}
                  />
                </li>
              ))}
            </ul>
            <MultiSelect
              label={t('boards.addTeamAccess')}
              options={(teams.data ?? []).map((team) => ({ value: team.id, label: team.name }))}
              value={teamAccess.map((g) => g.id)}
              onChange={(ids) => {
                setTeamAccess(
                  ids.map((id) => teamAccess.find((g) => g.id === id) ?? { id, role: 'editor' }),
                );
              }}
            />
          </div>

          <div className={styles.optionBlock}>
            <span className={styles.optionTitle}>{t('boards.roleAccess')}</span>
            <p className={styles.optionHint}>{t('boards.roleAccessHint')}</p>
            <ul className={styles.grantList}>
              {roleAccess.map((grant) => (
                <li key={grant.id} className={styles.grantRow}>
                  <span className={styles.grantName}>{roleName(grant.id)}</span>
                  <Combobox
                    label={t('access.roleOf', { name: roleName(grant.id) })}
                    value={grant.role}
                    size="sm"
                    options={roleOptions}
                    onChange={(next) => {
                      setRoleAccess(
                        roleAccess.map((g) =>
                          g.id === grant.id ? { ...g, role: next as MemberRole } : g,
                        ),
                      );
                    }}
                  />
                </li>
              ))}
            </ul>
            <MultiSelect
              label={t('boards.addRoleAccess')}
              options={[
                ...ASSIGNABLE_ROLES.map((role) => ({
                  value: `role:${role}`,
                  label: `${t(`roles.${role}`)} · ${t('workspaceAdmin.roleStandard')}`,
                })),
                ...(customRoles.data ?? []).map((role) => ({
                  value: `custom:${role.id}`,
                  label: `${role.name} · ${t('workspaceAdmin.roleCustom')}`,
                })),
              ]}
              value={roleAccess.map((g) => g.id)}
              onChange={(ids) => {
                setRoleAccess(
                  ids.map((id) => roleAccess.find((g) => g.id === id) ?? { id, role: 'editor' }),
                );
              }}
            />
          </div>

          <div className={styles.optionBlock}>
            <span className={styles.optionTitle}>{t('boards.initialParticipants')}</span>
            <ul className={styles.grantList}>
              {participants.map((grant) => (
                <li key={grant.id} className={styles.grantRow}>
                  <span className={styles.grantName}>{personName(grant.id)}</span>
                  <Combobox
                    label={t('access.roleOf', { name: personName(grant.id) })}
                    value={grant.role}
                    size="sm"
                    options={roleOptions}
                    onChange={(next) => {
                      setParticipants(
                        participants.map((g) =>
                          g.id === grant.id ? { ...g, role: next as MemberRole } : g,
                        ),
                      );
                    }}
                  />
                  <IconButton
                    label={t('boards.removeGrant', { name: personName(grant.id) })}
                    icon={<Icons.close />}
                    onClick={() => {
                      setParticipants(participants.filter((g) => g.id !== grant.id));
                    }}
                  />
                </li>
              ))}
            </ul>
            <UserSelect
              label={t('boards.addParticipant')}
              users={members.data ?? []}
              value={personPick}
              placeholder={t('boards.addParticipant')}
              exclude={new Set([...(viewerId ? [viewerId] : []), ...participants.map((g) => g.id)])}
              onChange={(next) => {
                setParticipants([...participants, { id: next, role: 'editor' }]);
                setPersonPick(null);
              }}
            />
          </div>
          <p className={styles.optionHint}>{t('boards.privateByDefault')}</p>
        </div>
      )}
      <FormError error={serverErrors.formError} />
      <DialogActions className={styles.createActions}>
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
        )}
        <Button type="submit" variant="primary" disabled={create.isPending}>
          {t('boards.create')}
        </Button>
      </DialogActions>
    </form>
  );
}
